/**
 * Kleda Client App — Risico Besluit View (bundle-aware, Fase 2)
 * Route: /risico/:token
 *
 * Klant heeft een mail ontvangen omdat we tijdens de voorinspectie iets
 * zagen op één of meerdere kleden uit dezelfde bestelling. Klant kiest per
 * kleed één van:
 *   - Ja, ga gerust door  (akkoord)
 *   - één van 3 alternatieven (onbehandeld retour / zelfhalen / afvoeren)
 *
 * "Nee, neem contact op" is vervangen door een expander die alternatieven
 * toont — minder telefoondrempel, meer self-service.
 */

import { useEffect, useMemo, useState } from 'react'
import { PageLayout } from './Layout'
import { RisicoKeuzeCards, type Alternatief } from './RisicoKeuzeCards'

interface BundleItem {
  wasserijItemId: string
  wiNummer: string | null
  kleedNaam: string | null
  risicoTypes: string[]
  toelichting: string | null
  /** AI-gepolijste klantvriendelijke variant; null = polish nog niet gedraaid */
  toelichtingKlantvriendelijk: string | null
  fotosUrl: string | null
  risicoStatus: string | null
  alreadyDecided: boolean
  klantReactie: string | null
  /** Fase 2: 3 alternatieven, alleen gevuld voor Wacht_klant items */
  alternatives?: Alternatief[] | null
}

interface RisicoBundleContext {
  klantNaam: string | null
  workOrderId: string | null
  workOrderNummer: string | null
  expired: boolean
  totaalKledenInWO: number
  kledenZonderRisico: number
  items: BundleItem[]
  /** Server-driven copy uit RisicoConsent.cls — één bron van waarheid */
  introParagraaf: string | null
  disclaimerTekst: string | null
  disclaimerVersie: string | null
}

interface ApiEnvelope<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string }
}

interface SubmitResultItem {
  wasserijItemId: string
  success: boolean
  risicoStatus: string | null
  message: string | null
  productCode?: string | null
  productNaam?: string | null
  effectivePrice?: number | null
  isMultiWiKorting?: boolean | null
}

interface Props {
  token: string
}

type Choice =
  | { kind: 'akkoord' }
  | { kind: 'alternatief'; productCode: string }
  | null

export function RisicoBesluitView({ token }: Props) {
  const [context, setContext] = useState<RisicoBundleContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reactie, setReactie] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [choices, setChoices] = useState<Record<string, Choice>>({})
  const [submitResults, setSubmitResults] = useState<SubmitResultItem[] | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    fetch(`/api/client-app/risico?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const json = (await r.json()) as ApiEnvelope<RisicoBundleContext>
        if (cancelled) return
        if (!json.success || !json.data) {
          setLoadError(json.error?.message || 'Kon dit voorstel niet laden')
        } else {
          setContext(json.data)
          const initial: Record<string, Choice> = {}
          for (const item of json.data.items) {
            if (!item.alreadyDecided) initial[item.wasserijItemId] = null
          }
          setChoices(initial)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Onbekende fout')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const pendingItems = useMemo(
    () => (context?.items ?? []).filter((i) => !i.alreadyDecided),
    [context]
  )
  const decidedItems = useMemo(
    () => (context?.items ?? []).filter((i) => i.alreadyDecided),
    [context]
  )
  const allAlreadyDecided =
    !!context && context.items.length > 0 && pendingItems.length === 0
  const allChosen =
    pendingItems.length > 0 &&
    pendingItems.every((i) => choices[i.wasserijItemId] != null)

  const setChoice = (wiId: string, choice: Choice) => {
    setChoices((prev) => ({ ...prev, [wiId]: choice }))
  }

  const handleJaVoorAllemaal = () => {
    setChoices((prev) => {
      const next: Record<string, Choice> = { ...prev }
      for (const item of pendingItems) next[item.wasserijItemId] = { kind: 'akkoord' }
      return next
    })
  }

  const handleSubmit = async () => {
    // Nog niet elk kleed heeft een keuze: breng de klant naar het eerste kleed
    // dat nog open staat in plaats van de knop dood te zetten. Bij meerdere
    // kleden staat dat kleed vaak buiten beeld.
    if (!allChosen) {
      const eerstOpen = pendingItems.find((i) => choices[i.wasserijItemId] == null)
      if (eerstOpen) {
        document
          .querySelector(`[data-kleed-id="${eerstOpen.wasserijItemId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      const decisions = pendingItems.map((item) => {
        const choice = choices[item.wasserijItemId]
        const base = {
          wasserijItemId: item.wasserijItemId,
          klantReactie: reactie.trim() || undefined,
        }
        if (choice?.kind === 'alternatief') {
          return { ...base, productCode: choice.productCode }
        }
        // Default: akkoord
        return { ...base, akkoord: true }
      })
      const r = await fetch('/api/client-app/risico-akkoord', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          decisions,
          disclaimerVersie: context?.disclaimerVersie ?? null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        }),
      })
      const json = (await r.json()) as ApiEnvelope<{ results: SubmitResultItem[] }>
      if (!json.success) {
        setSubmitError(json.error?.message || 'Keuzes konden niet worden opgeslagen')
        setSubmitting(false)
        return
      }
      setSubmitResults(json.data?.results ?? [])
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Keuzes konden niet worden opgeslagen')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Loading ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageLayout>
        <div className="py-2xl text-center text-ink-muted">Laden…</div>
      </PageLayout>
    )
  }

  // ─── Load error / not found ─────────────────────────────────────────────
  if (loadError || !context) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto py-xl">
          <div className="bg-white rounded-card p-lg border border-border-subtle text-center">
            <h1 className="text-heading-sub text-ink mb-sm">Hmm, dat lukte niet</h1>
            <p className="text-body text-ink-muted">
              {loadError || 'Kon dit voorstel niet vinden. Misschien is de link verlopen?'}
            </p>
            <p className="text-body-sm text-ink-muted mt-md">
              Bel ons op{' '}
              <a href="tel:+31882999277" className="text-primary underline">
                088 299 9277
              </a>{' '}
              dan helpen we je verder.
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  const voornaam = context.klantNaam ? context.klantNaam.split(' ')[0] : null
  const totaalRisico = context.items.length
  const isMulti = totaalRisico > 1

  // ─── Expired (en niemand al beslist) ────────────────────────────────────
  if (context.expired && !allAlreadyDecided) {
    return (
      <PageLayout orderNummer={context.workOrderNummer ?? undefined}>
        <div className="max-w-2xl mx-auto py-xl">
          <div className="bg-white rounded-card p-lg border border-border-subtle text-center">
            <h1 className="text-heading-sub text-ink mb-sm">Deze link is verlopen</h1>
            <p className="text-body text-ink-muted">
              We hebben nog niet gehoord wat je wilt — geen probleem. Bel of mail ons even
              dan pakken we het samen op.
            </p>
            <p className="text-body-sm text-ink-muted mt-md">
              <a href="tel:+31882999277" className="text-primary underline">
                088 299 9277
              </a>{' '}
              ·{' '}
              <a href="mailto:info@kleda.nl" className="text-primary underline">
                info@kleda.nl
              </a>
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  // ─── Success na submit ──────────────────────────────────────────────────
  if (submitResults) {
    const akkoordCount = submitResults.filter((r) => r.risicoStatus === 'Akkoord').length
    const altCount = submitResults.filter((r) => r.risicoStatus === 'Alternatief_gekozen').length
    return (
      <PageLayout orderNummer={context.workOrderNummer ?? undefined}>
        <div className="max-w-2xl mx-auto py-xl">
          <div className="bg-white rounded-card p-lg border border-border-subtle text-center space-y-md">
            <p className="text-heading-main-lg">🌿</p>
            <h1 className="text-heading-sub-lg text-ink">Bedankt!</h1>
            <p className="text-body text-ink-muted">
              {akkoordCount > 0 && altCount === 0 &&
                (akkoordCount === 1
                  ? 'We gaan ermee aan de slag. Zodra je vloerkleed klaar is hoor je het van ons.'
                  : `We gaan met alle ${akkoordCount} kleden aan de slag. Zodra ze klaar zijn hoor je het van ons.`)}
              {akkoordCount === 0 && altCount > 0 &&
                'We hebben je keuze ontvangen en regelen het zoals je hebt aangegeven.'}
              {akkoordCount > 0 && altCount > 0 &&
                `We gaan met ${akkoordCount} ${akkoordCount === 1 ? 'kleed' : 'kleden'} aan de slag. Voor ${altCount > 1 ? 'de andere kleden' : 'het andere kleed'} regelen we het zoals je hebt gekozen.`}
            </p>
          </div>
        </div>
      </PageLayout>
    )
  }

  // ─── Alle items al beslist (refresh / dubbele klik) ─────────────────────
  if (allAlreadyDecided) {
    return (
      <PageLayout orderNummer={context.workOrderNummer ?? undefined}>
        <div className="max-w-2xl mx-auto py-xl space-y-md">
          <div className="bg-white rounded-card p-lg border border-border-subtle text-center">
            <h1 className="text-heading-sub text-ink mb-sm">
              Je hebt al een keuze gemaakt{voornaam ? `, ${voornaam}` : ''} — bedankt!
            </h1>
            <p className="text-body text-ink-muted">Hieronder een overzicht.</p>
          </div>
          <div className="space-y-sm">
            {decidedItems.map((item) => {
              const wasAkkoord = item.risicoStatus === 'Akkoord'
              const wasAlt = item.risicoStatus === 'Alternatief_gekozen'
              return (
                <div
                  key={item.wasserijItemId}
                  className="bg-white rounded-card p-md border border-border-subtle"
                >
                  <p className="text-body font-semibold text-ink">
                    {item.kleedNaam}
                    {item.wiNummer ? (
                      <span className="text-ink-muted font-normal"> ({item.wiNummer})</span>
                    ) : null}
                  </p>
                  <p className="text-body-sm text-ink-muted mt-xs">
                    {wasAkkoord
                      ? '✓ Ga gerust door'
                      : wasAlt
                        ? 'Alternatief gekozen'
                        : 'We nemen contact op'}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </PageLayout>
    )
  }

  // ─── Ready: bundle-keuze-pagina ─────────────────────────────────────────
  return (
    <PageLayout orderNummer={context.workOrderNummer ?? undefined}>
      <div className="max-w-2xl mx-auto py-lg space-y-lg">
        {/* Intro */}
        <div className="text-center">
          <h1 className="text-heading-sub-lg text-ink mb-sm">
            Even sparren{voornaam ? `, ${voornaam}` : ''}
          </h1>
          {isMulti ? (
            <p className="text-body text-ink-muted">
              Bij de voorinspectie van{' '}
              <strong>
                {totaalRisico} van je {context.totaalKledenInWO || totaalRisico} kleden
              </strong>{' '}
              zagen we iets waar we het eerlijk over willen hebben. Hieronder zie je per kleed wat we
              hebben gezien — kies per kleed wat je wilt dat we doen.
            </p>
          ) : (
            <p className="text-body text-ink-muted">
              Bij de voorinspectie van <strong>je vloerkleed</strong> zagen we iets waar we het eerlijk
              over willen hebben voor we beginnen. Lees het rustig door en kies wat je wilt dat we doen.
            </p>
          )}
          {context.kledenZonderRisico > 0 && isMulti && (
            <p className="text-body-sm text-ink-muted mt-sm">
              {context.kledenZonderRisico === 1
                ? 'Het andere kleed in je bestelling pakken we gewoon op.'
                : `De andere ${context.kledenZonderRisico} kleden in je bestelling pakken we gewoon op.`}
            </p>
          )}
          {context.introParagraaf && (
            <p className="text-body text-ink-muted mt-md leading-relaxed">
              {context.introParagraaf}
            </p>
          )}
        </div>

        {/* Bulk shortcut bij multi */}
        {isMulti && (
          <div className="bg-canvas rounded-card p-md flex items-center justify-between gap-sm">
            <p className="text-body-sm text-ink-muted">
              Wil je voor alle {totaalRisico} kleden hetzelfde besluit?
            </p>
            <button
              type="button"
              onClick={handleJaVoorAllemaal}
              disabled={submitting}
              className="shrink-0 py-xs px-md bg-white hover:bg-primary/5 text-primary font-medium border border-primary rounded-full transition-colors text-body-sm disabled:opacity-50"
            >
              ✓ Ja voor allemaal
            </button>
          </div>
        )}

        {/* Per-kleed cards */}
        <div className="space-y-md">
          {pendingItems.map((item) => {
            const choice = choices[item.wasserijItemId]
            const specialistQuote = item.toelichtingKlantvriendelijk || item.toelichting

            return (
              <div
                key={item.wasserijItemId}
                data-kleed-id={item.wasserijItemId}
                className="bg-white rounded-card p-lg border border-border-subtle space-y-md"
              >
                <div className="flex items-start justify-between gap-sm">
                  <div>
                    <h2 className="text-heading-card text-ink">{item.kleedNaam || 'Vloerkleed'}</h2>
                    {item.wiNummer && (
                      <p className="text-body-sm text-ink-muted">{item.wiNummer}</p>
                    )}
                  </div>
                  {/* Status-badge alleen ná keuze */}
                  {choice?.kind === 'akkoord' && (
                    <span className="shrink-0 text-body-sm font-medium text-primary bg-primary/10 px-sm py-xs rounded-full">
                      ✓ Ja
                    </span>
                  )}
                  {choice?.kind === 'alternatief' && (
                    <span className="shrink-0 text-body-sm font-medium text-ink bg-canvas px-sm py-xs rounded-full border border-border-subtle">
                      Alternatief
                    </span>
                  )}
                  {!choice && (
                    <span className="shrink-0 text-body-sm font-medium text-ink-muted bg-canvas px-sm py-xs rounded-full">
                      Nog te kiezen
                    </span>
                  )}
                </div>

                {/* Wat we zagen — geconsolideerd in één blok met specialist-quote */}
                {(specialistQuote || item.risicoTypes.length > 0) && (
                  <div className="space-y-sm">
                    <p className="text-body-sm text-ink-muted uppercase tracking-wide">
                      Wat we zagen bij jouw kleed
                    </p>
                    {specialistQuote && (
                      <blockquote className="text-body text-ink italic whitespace-pre-wrap leading-relaxed border-l-4 border-primary/40 pl-md">
                        "{specialistQuote}"
                        <footer className="text-body-sm text-ink-muted not-italic mt-xs">
                          — Onze specialist bij de voorinspectie
                        </footer>
                      </blockquote>
                    )}
                    {item.risicoTypes.length > 0 && (
                      <div className="flex flex-wrap gap-xs pt-xs">
                        <span className="text-body-sm text-ink-muted">
                          Aandachtspunten:
                        </span>
                        {item.risicoTypes.map((type) => (
                          <span
                            key={type}
                            className="text-body-sm text-ink-muted bg-canvas px-sm py-xs rounded-full"
                          >
                            {type.toLowerCase()}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {item.fotosUrl && (
                  <a
                    href={item.fotosUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-xs text-primary underline font-medium text-body-sm"
                  >
                    📷 Bekijk de foto's van dit kleed
                  </a>
                )}

                {/* Disclaimer-zin boven keuze-cards — informed consent voor evidence trail */}
                {context.disclaimerTekst && (
                  <p className="text-body-sm text-ink-muted leading-relaxed">
                    {context.disclaimerTekst}
                  </p>
                )}

                {/* 4 keuze-cards: Ja + 3 alternatieven als gelijkwaardige opties */}
                <RisicoKeuzeCards
                  wasserijItemId={item.wasserijItemId}
                  alternatives={item.alternatives ?? []}
                  selected={choice}
                  onChoose={(c) => setChoice(item.wasserijItemId, c)}
                  disabled={submitting}
                />
              </div>
            )
          })}
        </div>

        {/* Reactie textarea (één voor de hele bundle) */}
        <div className="bg-white rounded-card p-md border border-border-subtle">
          <label htmlFor="risico-reactie" className="block text-body-sm text-ink-muted mb-sm">
            Wil je iets toevoegen? (optioneel{isMulti ? ' — geldt voor alle kleden' : ''})
          </label>
          <textarea
            id="risico-reactie"
            value={reactie}
            onChange={(e) => setReactie(e.target.value)}
            placeholder="Bv: 'Doe het rustig aan, en bel me bij twijfel.'"
            rows={3}
            maxLength={500}
            disabled={submitting}
            className="w-full p-sm border border-border-subtle rounded-md text-body text-ink focus:border-primary focus:outline-none resize-none disabled:opacity-50"
          />
          <p className="text-body-sm text-ink-muted mt-xs">{reactie.length}/500 tekens</p>
        </div>

        {/* Submit */}
        <div className="bg-white rounded-card p-lg border border-border-subtle">
          <p className="text-body text-ink text-center font-medium mb-md">
            {isMulti
              ? `Klaar? Stuur je keuzes door (${pendingItems.length} kleden)`
              : 'Klaar? Stuur je keuze door'}
          </p>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            aria-disabled={!allChosen || undefined}
            className={`w-full py-md px-lg bg-primary hover:bg-primary-dark text-white font-medium rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed${
              !allChosen ? ' opacity-70' : ''
            }`}
          >
            {submitting
              ? 'Bezig…'
              : !allChosen
                ? `Kies eerst per kleed (${pendingItems.filter((i) => choices[i.wasserijItemId] == null).length} te gaan)`
                : isMulti
                  ? 'Mijn keuzes doorsturen'
                  : 'Mijn keuze doorsturen'}
          </button>
          {submitError && (
            <p className="text-body-sm text-red-600 mt-sm text-center">{submitError}</p>
          )}
        </div>

        {/* Verwachtingsmanagement */}
        <div className="bg-canvas rounded-card p-md text-center space-y-xs">
          <p className="text-body-sm text-ink font-medium">Wat er daarna gebeurt</p>
          <p className="text-body-sm text-ink-muted">
            Je antwoord komt direct bij ons binnen. Bij <em>'Ja, ga gerust door'</em> plannen
            we je kleed meteen in voor reiniging.
          </p>
        </div>

        {/* Geruststelling */}
        <p className="text-body-sm text-ink-muted text-center">
          Geen zorgen — we doen niets totdat we van je horen.
        </p>
      </div>
    </PageLayout>
  )
}
